import { useEffect, useState } from 'react'
import { getAdminUrl, getMergedHeaders, useReapitConnectSession } from '@reapitform/hooks'

export type AdminAccessMode = 'readwrite' | 'readonly'

interface HealthResponse {
  status: string
  accessMode: AdminAccessMode
}

export type AdminHealthCheckResult =
  | { state: 'loading' }
  | { state: 'unauthorized' }
  | { state: 'unhealthy' }
  | { state: 'ready'; accessMode: AdminAccessMode }

/**
 * Probes GET /api/health once per session. This is more than a liveness check: the admin API sits behind a
 * whitelist-checking Lambda authorizer, so a 403 here means the signed-in user isn't on the admin whitelist
 * (not that the request itself was malformed) — the same signal the M1 whitelist gate originally used. Any
 * other failure (network error, non-2xx, unexpected body) is treated as a genuine outage rather than an auth
 * problem. On success, exposes the API's current accessMode ("readwrite"/"readonly") so the rest of the app
 * can gate edit/delete affordances off the exact same value ReadOnlyEnforcementMiddleware enforces.
 */
export const useAdminHealthCheck = (): AdminHealthCheckResult => {
  const session = useReapitConnectSession()
  const [result, setResult] = useState<AdminHealthCheckResult>({ state: 'loading' })

  useEffect(() => {
    let cancelled = false

    const checkHealth = async () => {
      try {
        const headers = await getMergedHeaders(session)
        if (!headers) {
          if (!cancelled) setResult({ state: 'unauthorized' })
          return
        }

        const response = await fetch(getAdminUrl('/api/health')(), { headers: headers as HeadersInit })

        if (response.status === 403) {
          if (!cancelled) setResult({ state: 'unauthorized' })
          return
        }

        if (!response.ok) {
          if (!cancelled) setResult({ state: 'unhealthy' })
          return
        }

        const body: HealthResponse = await response.json()
        if (body.status !== 'healthy') {
          if (!cancelled) setResult({ state: 'unhealthy' })
          return
        }

        // Default to 'readonly' if accessMode is missing or unexpected — safer than defaulting to
        // readwrite in a potentially read-only environment (e.g. bad deploy or proxy response).
        const accessMode: AdminAccessMode =
          body.accessMode === 'readwrite' || body.accessMode === 'readonly' ? body.accessMode : 'readonly'

        if (!cancelled) setResult({ state: 'ready', accessMode })
      } catch {
        if (!cancelled) setResult({ state: 'unhealthy' })
      }
    }

    checkHealth()

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return result
}
